import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import api, { formatError } from "../lib/api";
import {
  Coins, ArrowUpRight, ArrowDownRight, Clock, TrendingUp,
  Plus, Lock, CheckCircle, CreditCard, Zap, ArrowRight,
  Download, AlertCircle, Shield
} from "lucide-react";

const TX_ICONS = {
  topup: { icon: Plus, color: "var(--primary)", bg: "var(--accent-subtle)" },
  earning: { icon: ArrowDownRight, color: "var(--primary)", bg: "var(--accent-subtle)" },
  withdrawal: { icon: ArrowUpRight, color: "var(--pink)", bg: "var(--pink-subtle)" },
  lock: { icon: Lock, color: "var(--violet)", bg: "var(--violet-subtle)" },
  release: { icon: CheckCircle, color: "var(--primary)", bg: "var(--accent-subtle)" },
  refund: { icon: ArrowDownRight, color: "var(--violet)", bg: "var(--violet-subtle)" },
  commission: { icon: Shield, color: "var(--violet)", bg: "var(--violet-subtle)" },
  escrow: { icon: Lock, color: "var(--violet)", bg: "var(--violet-subtle)" },
  transfer_in: { icon: ArrowDownRight, color: "var(--primary)", bg: "var(--accent-subtle)" },
  transfer_out: { icon: ArrowUpRight, color: "var(--text-secondary)", bg: "var(--bg-soft)" },
};

const PRESET_AMOUNTS = [100, 250, 500, 1000];
const MIN_TOPUP = 100;
const CREDIT_TO_USD = 0.099;

function formatDate(isoStr) {
  if (!isoStr) return "";
  return new Date(isoStr).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function WalletPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupLoading, setTopupLoading] = useState(false);
  const [showTopup, setShowTopup] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cashfree");
  const [error, setError] = useState("");

  useEffect(() => { if (user === null) navigate("/login"); if (user) fetchData(); }, [user]);

  useEffect(() => {
    const orderId = searchParams.get("order_id");
    if (orderId && user) verifyPayment(orderId);
  }, [searchParams, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [w, t] = await Promise.all([api.get("/api/wallet/balance"), api.get("/api/wallet/transactions")]);
      setWallet(w.data); setTransactions(t.data.transactions || []);
    } catch (e) { console.error(formatError(e)); }
    finally { setLoading(false); }
  };

  const handleTopup = async () => {
    const amt = parseInt(topupAmount);
    if (!amt || amt < MIN_TOPUP) { setError(`Minimum top-up is ${MIN_TOPUP} credits`); return; }
    setTopupLoading(true); setError("");
    try {
      if (paymentMethod === "cashfree") {
        const { data } = await api.post("/api/wallet/create-payment", { amount: amt });
        if (data.payment_session_id) {
          try {
            const cashfree = window.Cashfree({ mode: "production" });
            await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
            return;
          } catch (sdkError) { setError("Payment gateway failed to load. Try again."); }
          return;
        }
        setError("Payment session could not be created.");
      } else {
        await api.post("/api/wallet/topup", { amount: amt });
      }
      setTopupAmount(""); setShowTopup(false); fetchData();
    } catch (e) { setError(formatError(e)); }
    finally { setTopupLoading(false); }
  };

  const verifyPayment = async (orderId) => {
    try {
      const { data } = await api.post(`/api/wallet/verify-payment/${orderId}`);
      if (data.status === "paid" || data.status === "already_credited") fetchData();
    } catch (e) { console.error(e); }
  };

  const handleWithdraw = async () => {
    const amt = parseInt(withdrawAmount);
    if (!amt || amt <= 0) return;
    setWithdrawLoading(true); setError("");
    try {
      await api.post("/api/wallet/withdraw", { amount: amt });
      setWithdrawAmount(""); setShowWithdraw(false); fetchData();
    } catch (e) { setError(formatError(e)); }
    finally { setWithdrawLoading(false); }
  };

  if (user === undefined) return null;
  const withdrawable = wallet?.withdrawable_balance || 0;

  return (
    <div style={{ paddingTop: 68, minHeight: "100vh" }}>
      <section style={{ position: "relative", padding: "80px 24px 40px", overflow: "hidden" }}>
        <div className="bg-blob" style={{ width: 400, height: 400, background: "rgba(0,92,238,0.03)", top: "-10%", left: "10%" }} />
        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 2 }}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", fontWeight: 600, color: "var(--primary)", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "0.8rem" }}>Credit Wallet</p>
            <h1 data-testid="wallet-page-title" style={{ fontFamily: "'Manrope', sans-serif", fontSize: "clamp(1.8rem, 4vw, 2.6rem)", fontWeight: 300, color: "var(--text-main)", letterSpacing: "-0.04em", marginBottom: 8 }}>
              Your <span style={{ fontWeight: 700 }}>credits</span>
            </h1>
            <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.9rem", color: "var(--text-secondary)", maxWidth: 450 }}>Credits power everything on TIE. Add credits to post tasks. Earn credits by executing.</p>
          </motion.div>
        </div>
      </section>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <div style={{ width: 40, height: 40, border: "3px solid rgba(0,92,238,0.08)", borderTop: "3px solid var(--primary)", borderRadius: "50%", margin: "0 auto", animation: "spin 0.8s linear infinite" }} />
        </div>
      ) : (
        <section style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px 80px" }}>
          <div data-testid="wallet-balance-cards" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 28 }}>
            {[
              { label: "Available", value: wallet?.balance || 0, accent: true, testId: "wallet-balance" },
              { label: "Secured", value: wallet?.locked_balance || 0, icon: Lock, iconColor: "var(--violet)", testId: "wallet-locked", sub: "In active tasks" },
              { label: "Earned", value: wallet?.earned_total || 0, icon: TrendingUp, iconColor: "var(--primary)", testId: "wallet-earned" },
              { label: "Withdrawable", value: withdrawable, icon: Download, iconColor: "var(--primary)", testId: "wallet-withdrawable" },
              { label: "Spent", value: wallet?.spent_total || 0, icon: ArrowUpRight, iconColor: "var(--text-muted)", testId: "wallet-spent" },
            ].map(({ label, value, accent, icon: Icon, iconColor, testId, sub }, i) => (
              <motion.div key={label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.05 }}
                className={accent ? "glass-card-accent" : "glass-card"} style={{ padding: "1.4rem" }}>
                <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", color: "var(--text-muted)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>{label}</p>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  {Icon && <Icon size={14} color={iconColor} strokeWidth={1.5} />}
                  <span data-testid={testId} style={{ fontFamily: "'Manrope', sans-serif", fontSize: accent ? "2rem" : "1.5rem", fontWeight: 700, color: accent ? "var(--text-main)" : iconColor || "var(--text-main)" }}>{value}</span>
                  {accent && <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.75rem", color: "var(--text-muted)" }}>credits</span>}
                </div>
                {sub && <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.62rem", color: "var(--text-muted)", marginTop: 3 }}>{sub}</p>}
              </motion.div>
            ))}
          </div>

          <AnimatePresence>
            {error && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                style={{ display: "flex", gap: 8, alignItems: "center", background: "var(--pink-subtle)", border: "1px solid rgba(255,51,102,0.15)", borderRadius: 10, padding: "0.6rem 0.9rem", marginBottom: 14 }}>
                <AlertCircle size={13} color="var(--pink)" />
                <span style={{ fontSize: "0.78rem", color: "var(--pink)", fontFamily: "'Satoshi', sans-serif" }}>{error}</span>
                <button onClick={() => setError("")} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "var(--pink)", fontSize: "0.72rem" }}>Dismiss</button>
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ display: "flex", gap: 10, marginBottom: 28 }}>
            <button data-testid="wallet-topup-btn" onClick={() => { setShowTopup(!showTopup); setShowWithdraw(false); setError(""); }} className="btn-primary" style={{ display: "flex", alignItems: "center", gap: 6 }}><Plus size={14} /> Add Credits</button>
            {withdrawable > 0 && (
              <button data-testid="wallet-withdraw-btn" onClick={() => { setShowWithdraw(!showWithdraw); setShowTopup(false); setError(""); }} className="btn-accent" style={{ display: "flex", alignItems: "center", gap: 6 }}><Download size={14} /> Withdraw</button>
            )}
            <button data-testid="wallet-find-tasks" onClick={() => navigate("/doer/feed")} className="btn-secondary" style={{ display: "flex", alignItems: "center", gap: 6 }}><Zap size={14} /> Earn Credits</button>
          </div>

          <AnimatePresence>
            {showTopup && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                className="glass-card" style={{ padding: "1.6rem", marginBottom: 28, overflow: "hidden" }}>
                <h3 style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: "0.95rem", marginBottom: 14 }}>Add Execution Credits</h3>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                  {PRESET_AMOUNTS.map(amt => (
                    <button key={amt} data-testid={`topup-preset-${amt}`} onClick={() => { setTopupAmount(String(amt)); setError(""); }}
                      style={{ padding: "0.5rem 1.1rem", borderRadius: 10, background: topupAmount === String(amt) ? "var(--accent-subtle)" : "var(--bg-soft)", border: `1.5px solid ${topupAmount === String(amt) ? "rgba(0,92,238,0.15)" : "transparent"}`, cursor: "pointer", fontFamily: "'Manrope', sans-serif", fontSize: "0.88rem", fontWeight: topupAmount === String(amt) ? 700 : 500, color: topupAmount === String(amt) ? "var(--accent-text)" : "var(--text-secondary)", transition: "all 200ms" }}>
                      {amt}
                    </button>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
                  <input data-testid="topup-input" type="number" min={MIN_TOPUP} value={topupAmount} onChange={e => { setTopupAmount(e.target.value); setError(""); }} placeholder={`Min ${MIN_TOPUP} credits`} className="form-input" style={{ width: 160 }} />
                  <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", color: "var(--text-muted)" }}>= ${((parseInt(topupAmount) || 0) * CREDIT_TO_USD).toFixed(2)} USD</span>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", fontWeight: 600, color: "var(--text-muted)", letterSpacing: "0.08em", marginBottom: 8 }}>PAYMENT METHOD</p>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[
                      { key: "cashfree", icon: CreditCard, label: "Cashfree (UPI, Cards, Netbanking)" },
                      { key: "simulated", icon: Zap, label: "Instant (Dev)" },
                    ].map(({ key, icon: Icon, label }) => (
                      <button key={key} data-testid={`pay-${key}`} onClick={() => setPaymentMethod(key)}
                        style={{ display: "flex", alignItems: "center", gap: 6, padding: "0.5rem 1rem", borderRadius: 10, background: paymentMethod === key ? "var(--accent-subtle)" : "var(--bg-soft)", border: `1.5px solid ${paymentMethod === key ? "rgba(0,92,238,0.15)" : "transparent"}`, cursor: "pointer", fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", fontWeight: paymentMethod === key ? 600 : 400, color: paymentMethod === key ? "var(--accent-text)" : "var(--text-secondary)" }}>
                        <Icon size={14} /> {label}
                      </button>
                    ))}
                  </div>
                </div>
                <button data-testid="topup-confirm-btn" onClick={handleTopup} disabled={topupLoading || !topupAmount || parseInt(topupAmount) < MIN_TOPUP} className="btn-primary"
                  style={{ opacity: topupLoading || !topupAmount || parseInt(topupAmount) < MIN_TOPUP ? 0.4 : 1, display: "flex", alignItems: "center", gap: 6, padding: "0.6rem 1.4rem" }}>
                  {topupLoading ? "Processing..." : `Add ${topupAmount || 0} credits`} <ArrowRight size={14} />
                </button>
                <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.68rem", color: "var(--text-muted)", marginTop: 10 }}>Minimum top-up: {MIN_TOPUP} credits (${(MIN_TOPUP * CREDIT_TO_USD).toFixed(2)} USD).</p>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showWithdraw && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                className="glass-card" style={{ padding: "1.6rem", marginBottom: 28, overflow: "hidden" }}>
                <h3 style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: "0.95rem", marginBottom: 14 }}>Withdraw Earnings</h3>
                <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-muted)", marginBottom: 14 }}>Available: <strong style={{ color: "var(--primary)" }}>{withdrawable} credits</strong></p>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <input data-testid="withdraw-input" type="number" min={1} max={withdrawable} value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} placeholder="Amount" className="form-input" style={{ width: 160 }} />
                  <button data-testid="withdraw-confirm-btn" onClick={handleWithdraw} disabled={withdrawLoading || !withdrawAmount || parseInt(withdrawAmount) > withdrawable} className="btn-primary" style={{ opacity: withdrawLoading || !withdrawAmount ? 0.4 : 1 }}>{withdrawLoading ? "Processing..." : "Request Withdrawal"}</button>
                </div>
                <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.68rem", color: "var(--text-muted)", marginTop: 10 }}>Withdrawals processed within 2-3 business days.</p>
              </motion.div>
            )}
          </AnimatePresence>

          <div>
            <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: "1.1rem", fontWeight: 600, marginBottom: 14, letterSpacing: "-0.02em" }}>Transaction History</h2>
            {transactions.length === 0 ? (
              <div className="glass-card" style={{ padding: "2.5rem", textAlign: "center" }}>
                <Coins size={28} color="var(--bg-neutral)" strokeWidth={1.5} style={{ marginBottom: 8 }} />
                <p style={{ fontFamily: "'Satoshi', sans-serif", color: "var(--text-muted)", fontSize: "0.88rem" }}>No transactions yet. Add credits to get started.</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {transactions.map((tx, i) => {
                  const cfg = TX_ICONS[tx.tx_type] || TX_ICONS.topup;
                  const IconComp = cfg.icon;
                  const isPositive = tx.amount > 0;
                  return (
                    <motion.div key={tx.id || i} data-testid={`tx-row-${i}`} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                      className="glass-card" style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: cfg.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <IconComp size={13} color={cfg.color} strokeWidth={1.5} />
                        </div>
                        <div>
                          <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", fontWeight: 500, color: "var(--text-main)" }}>{tx.description}</p>
                          <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.65rem", color: "var(--text-muted)" }}>{formatDate(tx.created_at)} \u00b7 {tx.tx_type.replace(/_/g, " ")}</p>
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "0.92rem", color: isPositive ? "var(--primary)" : "var(--pink)" }}>{isPositive ? "+" : ""}{tx.amount}</span>
                        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", color: "var(--text-muted)" }}>bal: {tx.balance_after}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
